import { resolveTenantThemeBinding } from '../adapters/tenantThemeAdapter';
import { themeRegistry } from './ThemeRegistry';
import { themeValidator } from './ThemeValidator';
import { ThemeLoader } from './ThemeLoader';
import { themeState } from '../state/themeState';
import { ENGINE_VERSION } from './types';
/**
 * ThemeEngine
 * ------------------------
 * The ONLY module Core is allowed to import for theming.
 * It composes Registry + Loader + Validator + State behind one small
 * public surface, so Core interacts with "the theme system" as a
 * single concept and never touches the pieces (or any theme) directly.
 */
export class ThemeEngine {
    constructor(options) {
        this.version = ENGINE_VERSION;
        /**
         * In-flight activation guard. Callers activating the same theme id
         * concurrently (e.g. ThemeProvider.initialize() and
         * TenantThemeProvider both resolving to "default" on boot) share a
         * single Promise and a single loader/state-write cycle instead of
         * racing two independent activations against the shared ThemeState.
         */
        this.activationPromises = new Map();
        this.registry = options.registry ?? themeRegistry;
        this.validator = options.validator ?? themeValidator;
        this.state = options.state ?? themeState;
        this.defaultThemeId = options.defaultThemeId;
        this.preferenceStore = options.preferenceStore;
        this.loader = new ThemeLoader(this.registry, this.validator, this.defaultThemeId);
    }
    /** Register a theme entry. Thin passthrough so Core has one surface for everything theme-related. */
    registerTheme(entry) {
        this.registry.register(entry);
    }
    /** List available themes (metadata only — no theme code is loaded by this call). */
    listAvailableThemes() {
        return this.registry.list();
    }
    /** The currently active, fully resolved theme — or null before the first activation. */
    getActiveTheme() {
        return this.state.getState().activeTheme;
    }
    /** Subscribe to theme state changes (active theme, loading, fallback flag). */
    subscribe(listener) {
        return this.state.subscribe(listener);
    }
    /**
     * Boot sequence: resolves the initial theme id (saved preference, or the
     * engine default), then activates it. Safe to call once at app startup.
     */
    async initialize() {
        const preferredId = (await this.preferenceStore?.getPreferredThemeId()) ?? this.defaultThemeId;
        return this.activateTheme(preferredId);
    }
    /**
     * Activates a theme by id. Always resolves — never throws to the caller
     * for an invalid/missing requested theme, because ThemeLoader guarantees
     * a safe fallback to the default theme. Only throws if even the default
     * theme is unavailable, which is a fatal configuration error.
     */
    async activateTheme(id) {
        const inFlight = this.activationPromises.get(id);
        if (inFlight) {
            return inFlight;
        }
        const promise = this.performActivation(id);
        this.activationPromises.set(id, promise);
        try {
            return await promise;
        }
        finally {
            this.activationPromises.delete(id);
        }
    }
    /**
     * Original activateTheme body. Not exposed directly — always go
     * through activateTheme() so the in-flight guard above applies.
     */
    async performActivation(id) {
        this.state.setState({ isLoading: true });
        const result = await this.loader.loadTheme(id);
        this.state.setState({
            activeTheme: result.theme,
            activeThemeId: result.theme.manifest.id,
            isLoading: false,
            fellBackToDefault: result.fellBackToDefault,
        });
        // Persist the *requested* id as the user's intent, not necessarily
        // the id that ended up active — a transient validation issue
        // shouldn't silently overwrite the user's real preference.
        if (!result.fellBackToDefault) {
            await this.preferenceStore?.setPreferredThemeId(id);
        }
        return result;
    }
    /**
     * Activates the theme for a tenant/customer workspace.
     *
     * Resolves `binding` to a concrete theme id via the existing tenant
     * resolution pipeline (`resolveTenantThemeBinding`, which itself
     * delegates to `resolveTenantTheme`), then activates that id through
     * the same `activateTheme` path used by every other caller -- no
     * activation logic is duplicated here.
     *
     * If `binding` is `undefined`/`null`, or its own fields don't yield a
     * usable theme id, resolution falls through the adapter/resolver's
     * existing priority chain (tenant theme -> tenant default -> system
     * fallback -> `"default"`) exactly as it does for any other caller of
     * `resolveTenantThemeBinding`. Applications that never call this
     * method are entirely unaffected.
     */
    async activateForTenant(binding) {
        const { resolvedThemeId } = resolveTenantThemeBinding(binding);
        return this.activateTheme(resolvedThemeId);
    }
    /** Force-clears the loader's in-memory cache (e.g. after a theme update). */
    clearThemeCache(id) {
        this.loader.clearCache(id);
    }
}
